/**
 * Security composable for handling various security measures
 */
import { ref } from 'vue'
import { RateLimiter, sanitizeInput, escapeHtml, validateFileUpload } from '@/utils/security'

export function useSecurity() {
  // Rate limiter instance (shared across the app)
  const rateLimiter = new RateLimiter(5, 15 * 60 * 1000) // 5 attempts per 15 minutes
  
  // Security state
  const isBlocked = ref(false)
  const blockTimeRemaining = ref(0)
  const lastAttempt = ref(null)

  /**
   * Check if an identifier is rate limited
   * @param {string} identifier - Usually email or IP address
   * @returns {boolean} - Whether the identifier is blocked
   */
  const checkRateLimit = (identifier) => {
    const blocked = rateLimiter.isBlocked(identifier)
    isBlocked.value = blocked
    
    if (blocked) {
      blockTimeRemaining.value = rateLimiter.getTimeUntilReset(identifier)
    }
    
    return !blocked
  }

  /**
   * Record a failed attempt
   * @param {string} identifier - Usually email or IP address
   */
  const recordFailedAttempt = (identifier) => {
    rateLimiter.recordAttempt(identifier)
    lastAttempt.value = new Date()
    
    // Update blocked status
    isBlocked.value = rateLimiter.isBlocked(identifier)
    if (isBlocked.value) {
      blockTimeRemaining.value = rateLimiter.getTimeUntilReset(identifier)
    }
  }

  /**
   * Reset rate limit for an identifier (e.g., on successful login)
   * @param {string} identifier - Usually email or IP address
   */
  const resetRateLimit = (identifier) => {
    rateLimiter.reset(identifier)
    isBlocked.value = false
    blockTimeRemaining.value = 0
  }

  /**
   * Sanitize form data to prevent XSS attacks
   * @param {object} formData - Form data object
   * @param {object} options - Sanitization options per field
   * @returns {object} - Sanitized form data
   */
  const sanitizeFormData = (formData, options = {}) => {
    const sanitized = {}
    
    for (const [key, value] of Object.entries(formData)) {
      if (typeof value === 'string') {
        const fieldOptions = options[key] || {
          allowHtml: false,
          maxLength: 1000,
          removeScripts: true,
          removeEvents: true
        }
        
        sanitized[key] = sanitizeInput(value, fieldOptions)
      } else {
        sanitized[key] = value
      }
    }
    
    return sanitized
  }

  /**
   * Escape HTML in display data to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} - Escaped text
   */
  const escapeForDisplay = (text) => {
    return escapeHtml(text)
  }

  /**
   * Validate file upload with security checks
   * @param {File} file - File to validate
   * @param {object} options - Validation options
   * @returns {object} - Validation result
   */
  const validateFile = (file, options = {}) => {
    return validateFileUpload(file, options)
  }

  /**
   * Generate a secure random token
   * @param {number} length - Token length
   * @returns {string} - Random token
   */
  const generateSecureToken = (length = 32) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let result = ''
    
    // Use crypto.getRandomValues if available (browser)
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint8Array(length)
      crypto.getRandomValues(array)
      
      for (let i = 0; i < length; i++) {
        result += chars[array[i] % chars.length]
      }
    } else {
      // Fallback to Math.random (less secure)
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length))
      }
    }
    
    return result
  }

  /**
   * Check if content contains potential security threats
   * @param {string} content - Content to check
   * @returns {object} - Security analysis result
   */
  const analyzeContentSecurity = (content) => {
    const threats = []
    const suspiciousPatterns = [
      { pattern: /<script[^>]*>/i, threat: 'Script tag detected' },
      { pattern: /javascript:/i, threat: 'JavaScript URL detected' },
      { pattern: /on\w+\s*=/i, threat: 'Event handler detected' },
      { pattern: /<iframe[^>]*>/i, threat: 'Iframe tag detected' },
      { pattern: /<object[^>]*>/i, threat: 'Object tag detected' },
      { pattern: /<embed[^>]*>/i, threat: 'Embed tag detected' },
      { pattern: /data:text\/html/i, threat: 'Data URL with HTML detected' },
      { pattern: /vbscript:/i, threat: 'VBScript URL detected' },
    ]

    suspiciousPatterns.forEach(({ pattern, threat }) => {
      if (pattern.test(content)) {
        threats.push(threat)
      }
    })

    const riskLevel = threats.length === 0 ? 'low' : 
                     threats.length <= 2 ? 'medium' : 'high'

    return {
      isSecure: threats.length === 0,
      riskLevel,
      threats,
      recommendation: threats.length > 0 ? 
        'Content contains potentially dangerous elements and should be reviewed.' :
        'Content appears safe.'
    }
  }

  /**
   * Format time remaining for rate limiting display
   * @param {number} ms - Milliseconds remaining
   * @returns {string} - Formatted time string
   */
  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return '0 seconds'
    
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}`
    }
    
    return `${seconds} second${seconds !== 1 ? 's' : ''}`
  }

  /**
   * Secure data storage with encryption (basic implementation)
   * @param {string} key - Storage key
   * @param {any} data - Data to store
   */
  const secureStorageSet = (key, data) => {
    try {
      // In a real app, you'd want to encrypt sensitive data
      const encoded = btoa(JSON.stringify(data))
      localStorage.setItem(`secure_${key}`, encoded)
    } catch (error) {
      console.warn('Failed to store data securely:', error)
    }
  }

  /**
   * Secure data retrieval with decryption
   * @param {string} key - Storage key
   * @returns {any} - Retrieved data
   */
  const secureStorageGet = (key) => {
    try {
      const encoded = localStorage.getItem(`secure_${key}`)
      if (!encoded) return null
      
      return JSON.parse(atob(encoded))
    } catch (error) {
      console.warn('Failed to retrieve data securely:', error)
      return null
    }
  }

  /**
   * Remove secure data from storage
   * @param {string} key - Storage key
   */
  const secureStorageRemove = (key) => {
    try {
      localStorage.removeItem(`secure_${key}`)
    } catch (error) {
      console.warn('Failed to remove secure data:', error)
    }
  }

  return {
    // Rate limiting
    isBlocked,
    blockTimeRemaining,
    checkRateLimit,
    recordFailedAttempt,
    resetRateLimit,
    formatTimeRemaining,
    
    // Data sanitization
    sanitizeFormData,
    escapeForDisplay,
    
    // File validation
    validateFile,
    
    // Security analysis
    analyzeContentSecurity,
    
    // Token generation
    generateSecureToken,
    
    // Secure storage
    secureStorageSet,
    secureStorageGet,
    secureStorageRemove,
  }
}